import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Bot, Loader2, RefreshCw, Sparkles } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  buildSpecialAgentAiContext,
  specialAgentAiContextCacheKey,
} from "@/utils/specialAgentAiInsight";

const InsightList = ({ title, items, isDark, accentClass }) => {
  if (!items?.length) return null;
  return (
    <div>
      <p className={cn("text-[10px] font-bold uppercase tracking-wide mb-1.5", accentClass)}>{title}</p>
      <ul className="space-y-1">
        {items.map((line, i) => (
          <li
            key={`${title}-${i}`}
            className={cn(
              "text-[11px] leading-relaxed pl-2.5 border-l-2",
              isDark ? "border-teal-500/40 text-slate-300" : "border-teal-400 text-gray-700",
            )}
          >
            {line}
          </li>
        ))}
      </ul>
    </div>
  );
};

const SarOverviewAiInsight = ({
  apiBase,
  bundle,
  tlKaliteAlignment,
  enabled,
  isDark,
  textClass,
  labelClass,
}) => {
  const context = useMemo(
    () => (enabled ? buildSpecialAgentAiContext(bundle, tlKaliteAlignment) : null),
    [bundle, tlKaliteAlignment, enabled],
  );
  const cacheKey = useMemo(() => specialAgentAiContextCacheKey(context), [context]);

  const [loading, setLoading] = useState(false);
  const [insight, setInsight] = useState(null);
  const [source, setSource] = useState(null);
  const [note, setNote] = useState("");
  const [error, setError] = useState("");

  const load = useCallback(async (skipCache = false) => {
    if (!context || !apiBase) return;
    if (!skipCache && cacheKey) {
      try {
        const raw = sessionStorage.getItem(cacheKey);
        if (raw) {
          const cached = JSON.parse(raw);
          if (cached?.insight) {
            setInsight(cached.insight);
            setSource(cached.source);
            setNote(cached.note || "");
            return;
          }
        }
      } catch {
        /* ignore */
      }
    }

    setLoading(true);
    setError("");
    try {
      const res = await axios.post(`${apiBase}/special-agent-ai-insight`, { context }, { timeout: 120000 });
      const data = res.data || {};
      setInsight(data.insight || null);
      setSource(data.source || null);
      setNote(data.note || "");
      if (cacheKey && data.insight) {
        sessionStorage.setItem(
          cacheKey,
          JSON.stringify({ insight: data.insight, source: data.source, note: data.note }),
        );
      }
    } catch (err) {
      setError(err.response?.data?.detail || err.message || "AI yorum alınamadı");
      setInsight(null);
    } finally {
      setLoading(false);
    }
  }, [apiBase, context, cacheKey]);

  useEffect(() => {
    if (!enabled || !context) {
      setInsight(null);
      return;
    }
    load(false);
  }, [enabled, context, load]);

  if (!enabled) {
    return (
      <p className={cn("text-[11px]", labelClass)}>
        AI yorumu için TL ve Kalite kayıtları gerekir.
      </p>
    );
  }

  return (
    <div
      className={cn(
        "sar-v2-ai-insight rounded-xl border p-3 flex-1 min-h-[140px] flex flex-col",
        isDark ? "border-teal-500/25 bg-slate-950/45" : "border-teal-200/80 bg-white/90",
      )}
    >
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="flex items-center gap-2">
          <div className={cn("p-1 rounded-md", isDark ? "bg-teal-500/15" : "bg-teal-50")}>
            <Bot className={cn("w-3.5 h-3.5", isDark ? "text-teal-300" : "text-teal-700")} />
          </div>
          <span className={cn("text-xs font-bold", textClass)}>AI analiz yorumu</span>
          {source === "claude" ? (
            <span className={cn("text-[9px] px-1.5 py-0.5 rounded-full", isDark ? "bg-orange-500/20 text-orange-200" : "bg-orange-100 text-orange-900")}>
              Claude
            </span>
          ) : source === "gemini" ? (
            <span className={cn("text-[9px] px-1.5 py-0.5 rounded-full", isDark ? "bg-violet-500/20 text-violet-200" : "bg-violet-100 text-violet-800")}>
              Gemini
            </span>
          ) : source === "rule_based" ? (
            <span className={cn("text-[9px] px-1.5 py-0.5 rounded-full", isDark ? "bg-slate-700 text-slate-300" : "bg-gray-100 text-gray-600")}>
              Otomatik
            </span>
          ) : null}
        </div>
        <button
          type="button"
          onClick={() => load(true)}
          disabled={loading}
          className={cn(
            "p-1 rounded-md transition-colors",
            isDark ? "hover:bg-slate-800 text-slate-400" : "hover:bg-gray-100 text-gray-500",
          )}
          title="Yeniden oluştur"
        >
          {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
        </button>
      </div>

      {loading && !insight ? (
        <div className={cn("flex-1 flex flex-col justify-center gap-2 py-4", labelClass)}>
          <Sparkles className={cn("w-5 h-5 animate-pulse mx-auto", isDark ? "text-teal-400" : "text-teal-600")} />
          <p className="text-[11px] text-center">Değerlendirmeler analiz ediliyor…</p>
        </div>
      ) : null}

      {error ? (
        <p className="text-[11px] text-rose-500">{error}</p>
      ) : null}

      {insight ? (
        <div className={cn("space-y-3 flex-1 overflow-auto max-h-[320px] pr-1", loading && "opacity-60")}>
          <p className={cn("text-[11px] leading-relaxed", textClass)}>{insight.summary}</p>
          {insight.coaching_focus ? (
            <p
              className={cn(
                "text-[11px] font-medium rounded-lg px-2 py-1.5",
                isDark ? "bg-amber-500/10 text-amber-100 border border-amber-500/20" : "bg-amber-50 text-amber-950 border border-amber-200",
              )}
            >
              <span className="font-bold">Odak: </span>
              {insight.coaching_focus}
            </p>
          ) : null}
          <InsightList
            title="Ortak noktalar"
            items={insight.common_ground}
            isDark={isDark}
            accentClass={isDark ? "text-emerald-400" : "text-emerald-700"}
          />
          <InsightList
            title="Farklılıklar"
            items={insight.differences}
            isDark={isDark}
            accentClass={isDark ? "text-rose-400" : "text-rose-700"}
          />
          <InsightList
            title="Öneriler"
            items={insight.recommendations}
            isDark={isDark}
            accentClass={isDark ? "text-cyan-400" : "text-cyan-800"}
          />
          {note ? <p className={cn("text-[10px] italic", labelClass)}>{note}</p> : null}
        </div>
      ) : null}
    </div>
  );
};

export default SarOverviewAiInsight;
